import _ from "lodash";
import Vue from "vue";

import * as k4util from "@/utils/k4util";

import nodesLive from "./impl/nodes-live";
import nodesProvision from "./impl/nodes-provision";
import nodesSmartMode from "./impl/smart-mode";

const getInitState = () => ({
	nodes: {},
});

export default {
	namespaced: true,
	state: getInitState(),
	modules: {
		live: nodesLive,
		provision: nodesProvision,
		smartMode: nodesSmartMode,
	},

	getters: {
		$parent(state, getters, rootState) {
			return rootState.controller;
		},

		$model(state, getters) {
			return _.get(getters.$parent, "model.model", null);
		},

		nodeByGuid: (state, getters, rootState) => guid => {
			return rootState.controller.local
				? getters.nodeByGuidProvision(guid)
				: getters.nodeByGuidLive(guid);
		},

		// Cache paths for each node we have a guid for
		paths: state => {
			return _.reduce(
				state.nodes,
				(accum, node) => {
					accum[node.path] = node.meta.guid;
					return accum;
				},
				{}
			);
		},

		nodeByPath: (state, getters) => path => {
			const guid = getters.paths[path];
			return guid ? getters.nodeByGuid(guid) : null;
		},

		nodes: (state, getters, rootState) => {
			const excluded = rootState.descriptions.exclude;
			return _.sortBy(
				_.filter(
					_.values(state.nodes),
					node => excluded.indexOf(node.meta.group) === -1
				),
				"name" // Sort by name
			);
		},

		controller: (state, getters) => {
			return _.find(getters.nodes, {
				meta: { cls: k4util.DEVICE_CLASS_CONTROLLER },
			});
		},

		rooms: (state, getters) =>
			_.map(
				_.filter(getters.nodes, {
					meta: { cls: k4util.DEVICE_CLASS_ROOM },
				}),
				room => ({
					...room,
					children: _.filter(getters.nodes, {
						meta: { parent: room.meta.guid },
					}),
				})
			),

		devicesByType: (state, getters) => type =>
			_.filter(getters.nodes, node => _.has(node, type)),

		thermostats: (state, getters) =>
			_.filter(getters.nodes, {
				meta: { group: "thermostats" },
			}),

		actionServer: state =>
			_.find(state.nodes, {
				meta: { cls: k4util.DEVICE_CLASS_ACTION_SERVER },
			}),

		actions: state =>
			_.filter(state.nodes, {
				meta: { group: k4util.NODE_GROUP_ACTION },
			}),

		liveNodeByGuid: (state, getters) => guid => getters.$model.guids[guid],

		liveNodeByPath: (state, getters) => path => getters.$model.child(path),
	},

	mutations: {
		setNodes(state, nodes) {
			state.nodes = nodes;
		},

		resetState(state) {
			Object.assign(state, getInitState());
		},

		remove(state, guid) {
			Vue.delete(state.nodes, guid);
		},
	},

	actions: {
		modelChanged: {
			root: true,
			handler({ commit, rootState }) {
				const k4model = _.get(rootState, "controller.model.model", null);

				if (_.isNil(k4model)) commit("resetState");
				else commit("setNodes", _buildNodes(k4model));
			},
		},

		add(ctx, { name, parent, description, subpath }) {
			const cls = _.get(description, "meta.cls", null);
			this._vm.$log(
				parent
					? `Nodes: Adding node ${name} with class ${cls} to ${parent} and subpath ${subpath}`
					: `Nodes: Adding node ${name} with class ${cls} to subpath ${subpath}`
			);
			return _dispatch(ctx, {
				action: "add",
				payload: { name: (name || "").trim(), parent, description, subpath },
			});
		},

		remove(ctx, node) {
			this._vm.$log(`Nodes: Remove node ${node.name} at ${node.path}`);
			return _dispatch(ctx, { action: "remove", payload: { node } });
		},

		rename(ctx, { node, name }) {
			this._vm.$log(`Nodes: Rename node '${node.path}' to ${name}`);

			if (_.isString(name) && name.includes("/"))
				throw new Error("Name contains invalid character.");

			return _dispatch(ctx, {
				action: "rename",
				payload: { node, name },
			});
		},

		move(ctx, { node, destination }) {
			this._vm.$log(`Nodes: Move node ${node.name} to ${destination}`);

			return _dispatch(ctx, {
				action: "move",
				payload: { node, destination },
			});
		},

		setProperty(ctx, { nodeGuid, propName, propValue }) {
			const { getters } = ctx;
			const node = getters.nodeByGuid(nodeGuid);

			this._vm.$log(
				`Nodes: Setting '${node.path}/properties/${propName}' to value ${propValue}`
			);

			return _dispatch(ctx, {
				action: "setProperty",
				payload: { node, propName, propValue },
			});
		},

		setProperties({ dispatch }, { nodeGuid, properties }) {
			return Promise.all(
				_.map(properties, (val, key) =>
					dispatch("setProperty", {
						nodeGuid,
						propName: key,
						propValue: val,
					})
				)
			);
		},

		// Execute command on live K4 model
		// On success returns 'true' for simple ACK response, otherwise the full
		// response object is returned. An exception is thrown on error.
		executeCommand(ctx, { node, subpath, commandParams }) {
			const cmdNodePath = `${node.path}/${subpath}`;
			window.vm.$log(
				`Executing command ${cmdNodePath} with arguments: ${JSON.stringify(
					commandParams
				)}`
			);

			return _dispatch(ctx, {
				action: "executeCommand",
				payload: { node, subpath, commandParams },
			});
		},
	},
};

function _dispatch({ dispatch, rootState }, { action, payload }) {
	const isLocal = rootState.controller.local;
	const dispatchTo = isLocal ? `${action}Provision` : `${action}Live`;

	return dispatch(dispatchTo, payload).catch(e => {
		let parentException, message;
		if (_.isString(e)) {
			message = e;
		} else if (e.message) {
			message = e.message;
		}

		if (e instanceof Error) parentException = e;

		const wrappedErr = new k4util.NodeOperationError(
			action,
			payload.node,
			message,
			parentException
		);

		// Log and submit to BugSnag
		window.vm.$log(wrappedErr.toString());
		window.vm.$bugsnagClient.notify(wrappedErr);

		throw wrappedErr;
	});
}

function _buildNodes(k4model) {
	let nodes = _.cloneDeep(k4model.guids);
	const childMap = {};
	nodes = _.mapValues(nodes, node => {
		// Pass `true` to include the node's `meta` block
		let renderedNode = node.value(true);
		renderedNode = _.pickBy(renderedNode, (_, key) =>
			["commands", "properties", "variables", "events", "meta"].includes(key)
		);

		// Set 'configured' value if all required props are set
		renderedNode.configured = _getConfigured(renderedNode);

		renderedNode.name = node.name();
		renderedNode.path = node.path();

		// Map child/parent relationship
		const parent = k4util.closestParentDevice(node);
		if (parent) {
			const parentGuid = parent.guid();
			if (!childMap[parentGuid]) childMap[parentGuid] = [];
			childMap[parentGuid].push(node.guid());
			renderedNode.meta.parent = parentGuid;
		}

		return renderedNode;
	});

	// Make another pass to assign gathered children for each node
	nodes = _.mapValues(nodes, node => {
		node.children = childMap[node.meta.guid] || [];
		return node;
	});

	return nodes;
}

function _getConfigured(node) {
	return _.every(node.meta.properties, (meta, name) => {
		const required = meta.required;
		const val = _.get(node.properties, name);

		return !required || (required && val);
	});
}
